import { useEffect, useState, useRef } from "react";
import {
    Navigate, Route, Routes, useNavigate, useSearchParams
} from "react-router-dom";

import CookieConsent from "react-cookie-consent";
import { HeaderBar, NavigationBar, PrivateRoute } from "./components";
import { useAtom } from "@dbeining/react-atom";
import { Container, Image, Spinner, Stack } from "react-bootstrap";
import { accessDeniedAtom, updateUserStateAtomToLocalStorage, userAtom } from "./atoms";
import { SidebarLayoutProvider, useViewport } from "./contexts";
import { AcceptInvitationPage } from "./pages/AcceptInvitationPage/AcceptInvitationPage";
import AccessDeniedPage from "./pages/AccessDeniedPage/AccessDeniedPage";
import { AdminFoundationHubPage } from "./pages/AdminFoundationHubPage/AdminFoundationHubPage";
import { AdminPage } from "./pages/AdminPage/AdminPage";
import { AllTasksPage } from "./pages/AllTasksPage/AllTasksPage";
import { CalendarPage } from "./pages/CalendarPage/CalendarPage";
import ChecklistSettingsPage from "./pages/ChecklistSettingsPage/ChecklistSettingsPage";
import { CompanyInfoPage } from "./components/features/company";
import { DocumentLibraryPage } from "./pages/DocumentLibraryPage/DocumentLibraryPage";
import EmailTemplateEditPage from "./pages/EmailTemplate/EmailTemplateEditPage";
import { EmailTemplateListPage } from "./pages/EmailTemplate/EmailTemplateListPage";
import { ForgotPasswordPage } from "./pages/ForgotPasswordPage/ForgotPasswordPage";
import { InvitationsPage } from "./pages/InvitationsPage/InvitationsPage";
import { MyCalendarPage } from "./pages/MyCalendarPage/MyCalendarPage";
import { DashboardPage } from "./pages/DashboardPage/DashboardPage";
import { OperationsHubPage } from "./pages/OperationsHubPage/OperationsHubPage";
import { MovementPartiesHubPage } from "./pages/MovementPartiesHubPage/MovementPartiesHubPage";
import { ReferenceDataHubPage } from "./pages/ReferenceDataHubPage/ReferenceDataHubPage";
import { SystemHubPage } from "./pages/SystemHubPage/SystemHubPage";
import { LocationsListPage } from "./pages/LocationsListPage/LocationsListPage";
import { LocationAssociationsPage } from "./pages/LocationEditPage/LocationAssociationsPage";
import { LocationEditFormView } from "./pages/LocationEditPage/LocationEditFormView";
import { LocationEditLayout } from "./pages/LocationEditPage/LocationEditLayout";
import { LocationEditPage } from "./pages/LocationEditPage/LocationEditPage";
import { PlaceholderPage } from "./pages/PlaceholderPage/PlaceholderPage";
import { OrganisationsPage } from "./pages/OrganisationsPage/OrganisationsPage";
import { ResetPasswordPage } from "./pages/ResetPasswordPage/ResetPasswordPage";
import { SessionsPage } from "./pages/SessionsPage/SessionsPage";
import { SignInPage } from "./pages/SignInPage/SignInPage";
import { SettingsPage } from "./pages/SettingsPage/SettingsPage";
import { SiteEditPage } from "./pages/SiteEditPage/SiteEditPage";
import { appRoutes } from "./routes/appRoutes";
import { legacyRouteElements } from "./routes/legacyRouteRedirects";
import { referenceAdminRoles } from "./routes/referenceAdminRoutes";
import { referenceAdminAppRoutes } from "./routes/ReferenceAdminAppRoutes";
import { SitesListPage } from "./pages/SitesListPage/SitesListPage";
import { VehiclesListPage } from "./pages/VehiclesListPage/VehiclesListPage";
import { VehicleEditPage } from "./pages/VehicleEditPage/VehicleEditPage";
import { TippersListPage } from "./pages/TippersListPage/TippersListPage";
import { TipperEditPage } from "./pages/TipperEditPage/TipperEditPage";
import { HauliersListPage } from "./pages/HauliersListPage/HauliersListPage";
import { HaulierEditPage } from "./pages/HaulierEditPage/HaulierEditPage";
import { ProcessorsListPage } from "./pages/ProcessorsListPage/ProcessorsListPage";
import { ProcessorEditPage } from "./pages/ProcessorEditPage/ProcessorEditPage";
import { CustomersListPage } from "./pages/CustomersListPage/CustomersListPage";
import { CustomerEditLayout } from "./pages/CustomerEditPage/CustomerEditLayout";
import { CustomerEditFormView } from "./pages/CustomerEditPage/CustomerEditFormView";
import { CustomerLocationsPage } from "./pages/CustomerEditPage/CustomerLocationsPage";
import { CustomerNewLocationPage } from "./pages/CustomerEditPage/CustomerNewLocationPage";
import { CustomerEditPage } from "./pages/CustomerEditPage/CustomerEditPage";
import { TaskDetailPage } from "./pages/Task/TaskDetailPage";
import { TaskListPage } from "./pages/Task/TaskListPage";
import { TwoFASetupPage } from "./pages/TwoFASetupPage/TwoFASetupPage";
import { UserPage } from "./pages/UserPage/UserPage";
import { UsersPage } from "./pages/UsersPage/UsersPage";
import { WebsiteSettingListPage } from "./pages/WebsiteSetting/WebsiteSettingListPage";
import { authenticationService } from "./services";
import "react-toastify/dist/ReactToastify.css";
import "./styles/toastify-overrides.css";
import "./styles/App.css";
import { ToastContainer } from "react-toastify";

const adminRoles = referenceAdminRoles;

const App = () => {
    let navigate = useNavigate();
    let [searchParams] = useSearchParams();
    const id: string = searchParams.get("id") || "";
    const currentActionAccessDenied = useAtom(accessDeniedAtom);
    const [loadingUser, setLoadingUser] = useState(true);
    const currentUser = useAtom(userAtom);
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const lastManualState = useRef<boolean | null>(null);

    const { width } = useViewport();
    const lastWidth = useRef<number>(width);
    const breakpoint = 768;

    useEffect(() => {
        const wasInMediumRange = lastWidth.current >= 768 && lastWidth.current < 1065;
        const wasInLargeRange = lastWidth.current >= 1065;
        const wasInMobileRange = lastWidth.current < 768;

        const isInMediumRange = width >= 768 && width < 1065;
        const isInLargeRange = width >= 1065;
        const isInMobileRange = width < 768;

        if (isInMobileRange && !wasInMobileRange) {
            setIsSidebarCollapsed(false);
        } else if (isInMediumRange && !wasInMediumRange) {
            setIsSidebarCollapsed(true);
            lastManualState.current = null;
        } else if (isInLargeRange && !wasInLargeRange) {
            setIsSidebarCollapsed(false);
            lastManualState.current = null;
        } else if (isInMediumRange && wasInMobileRange) {
            if (lastManualState.current !== null) {
                setIsSidebarCollapsed(lastManualState.current);
            } else {
                setIsSidebarCollapsed(true);
            }
        } else if (isInLargeRange && wasInMobileRange) {
            if (lastManualState.current !== null) {
                setIsSidebarCollapsed(lastManualState.current);
            } else {
                setIsSidebarCollapsed(false);
            }
        }

        lastWidth.current = width;
    }, [width]);

    useEffect(() => {
        if (width >= 768) {
            const offcanvasElement = document.querySelector('.offcanvas.show');
            if (offcanvasElement) {
                const offcanvas = (window as any).bootstrap?.Offcanvas.getInstance(offcanvasElement);
                if (offcanvas) {
                    offcanvas.hide();
                }
            }
        }
    }, [width]);

    useEffect(() => {
        updateUserStateAtomToLocalStorage();

        authenticationService.checkAuthorised().then(() => {
            setLoadingUser(false);
        });
    }, []);

    useEffect(() => {
        if (currentActionAccessDenied) {
            navigate("/accessdenied", { replace: true });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentActionAccessDenied]);

    if (loadingUser) {
        return (
            <div className="d-flex align-content-center justify-content-center flex-wrap vh-100">
                <Stack className="justify-content-center mb-5" gap={3}>
                    <Image
                        src="/images/froom-waste-tracker-logo.svg"
                        className="mx-auto froom-logo"
                    />
                    <Spinner animation={"border"} variant="info" className="mx-auto" />
                </Stack>
            </div>
        );
    }

    const isFullyAuthenticated = currentUser &&
        (!currentUser.advisories || !currentUser.advisories.twoFaSetupRequired);

    const sidebarWidth = isSidebarCollapsed ? "5.5rem" : "17rem";

    const handleTransitionStart = () => {
        setIsTransitioning(true);
    };

    const handleTransitionEnd = () => {
        setIsTransitioning(false);
    };

    const handleSidebarToggle = (isCollapsed: boolean) => {
        setIsSidebarCollapsed(isCollapsed);
        lastManualState.current = isCollapsed;
    };

    return (
        <SidebarLayoutProvider value={{ isSidebarCollapsed }}>
        <>
            <div className="d-flex flex-nowrap min-height-100vh">
                {isFullyAuthenticated && width >= breakpoint && (
                    <div
                        className="d-flex flex-column width-ease"
                        style={{
                            width: sidebarWidth
                        }}
                    >
                        <NavigationBar
                            setShowMenu={() => { }}
                            onCollapsedChange={handleSidebarToggle}
                            onTransitionStart={handleTransitionStart}
                            onTransitionEnd={handleTransitionEnd}
                            isCollapsed={isSidebarCollapsed}
                        />
                    </div>
                )}

                <div className="flex-column flex-grow-1 flex-1">
                    {isFullyAuthenticated && (
                        <div id="header-container" className={isTransitioning ? "transitioning" : ""}>
                            <HeaderBar
                                isSidebarCollapsed={isSidebarCollapsed}
                                setIsSidebarCollapsed={setIsSidebarCollapsed}
                                onTransitionStart={handleTransitionStart}
                                onTransitionEnd={handleTransitionEnd}
                            />
                        </div>
                    )}
                    <Container fluid className={isFullyAuthenticated && width >= breakpoint ? "p-4" : "p-0"} style={{
                        ...(isFullyAuthenticated && width >= breakpoint ? {
                            float: "right",
                            width: `calc(100% - ${sidebarWidth})`,
                            transition: "width 0.3s ease"
                        } : {})
                    }}>
                        <Routes>
                            <Route path="/" element={<Navigate to="/dashboard" replace />} />
                            <Route path="/dashboard" element={
                                <PrivateRoute>
                                    <DashboardPage />
                                </PrivateRoute>
                            } />
                            <Route path="/operations" element={
                                <PrivateRoute>
                                    <OperationsHubPage />
                                </PrivateRoute>
                            } />
                            <Route path={appRoutes.transportParties} element={
                                <PrivateRoute>
                                    <MovementPartiesHubPage />
                                </PrivateRoute>
                            } />
                            <Route path={appRoutes.codes} element={
                                <PrivateRoute>
                                    <ReferenceDataHubPage />
                                </PrivateRoute>
                            } />
                            <Route path="/system" element={
                                <PrivateRoute>
                                    <SystemHubPage />
                                </PrivateRoute>
                            } />
                            <Route path={appRoutes.systemSetup} element={
                                <PrivateRoute roles={adminRoles}>
                                    <AdminFoundationHubPage />
                                </PrivateRoute>
                            } />
                            <Route path="/sites/new" element={
                                <PrivateRoute roles={adminRoles}>
                                    <SiteEditPage />
                                </PrivateRoute>
                            } />
                            <Route path="/sites/:id" element={
                                <PrivateRoute roles={adminRoles}>
                                    <SiteEditPage />
                                </PrivateRoute>
                            } />
                            <Route path="/sites" element={
                                <PrivateRoute>
                                    <SitesListPage />
                                </PrivateRoute>
                            } />
                            <Route path="/customers/new" element={
                                <PrivateRoute>
                                    <CustomerEditPage />
                                </PrivateRoute>
                            } />
                            <Route path="/customers/:id" element={
                                <PrivateRoute>
                                    <CustomerEditLayout />
                                </PrivateRoute>
                            }>
                                <Route index element={<CustomerEditFormView />} />
                                <Route path="locations" element={<CustomerLocationsPage />} />
                                <Route path="new-location" element={<CustomerNewLocationPage />} />
                            </Route>
                            <Route path="/customers" element={
                                <PrivateRoute>
                                    <CustomersListPage />
                                </PrivateRoute>
                            } />
                            <Route path="/locations/new" element={
                                <PrivateRoute>
                                    <LocationEditPage />
                                </PrivateRoute>
                            } />
                            <Route path="/locations/:id" element={
                                <PrivateRoute>
                                    <LocationEditLayout />
                                </PrivateRoute>
                            }>
                                <Route index element={<LocationEditFormView />} />
                                <Route path="associations" element={<LocationAssociationsPage />} />
                            </Route>
                            <Route path="/locations" element={
                                <PrivateRoute>
                                    <LocationsListPage />
                                </PrivateRoute>
                            } />
                            <Route path="/vehicles/new" element={
                                <PrivateRoute>
                                    <VehicleEditPage />
                                </PrivateRoute>
                            } />
                            <Route path="/vehicles/:id" element={
                                <PrivateRoute>
                                    <VehicleEditPage />
                                </PrivateRoute>
                            } />
                            <Route path="/vehicles" element={
                                <PrivateRoute>
                                    <VehiclesListPage />
                                </PrivateRoute>
                            } />
                            <Route path="/hauliers" element={
                                <PrivateRoute>
                                    <HauliersListPage />
                                </PrivateRoute>
                            } />
                            <Route path="/hauliers/new" element={
                                <PrivateRoute>
                                    <HaulierEditPage />
                                </PrivateRoute>
                            } />
                            <Route path="/hauliers/:id" element={
                                <PrivateRoute>
                                    <HaulierEditPage />
                                </PrivateRoute>
                            } />
                            <Route path="/tippers" element={
                                <PrivateRoute>
                                    <TippersListPage />
                                </PrivateRoute>
                            } />
                            <Route path="/tippers/new" element={
                                <PrivateRoute>
                                    <TipperEditPage />
                                </PrivateRoute>
                            } />
                            <Route path="/tippers/:id" element={
                                <PrivateRoute>
                                    <TipperEditPage />
                                </PrivateRoute>
                            } />
                            <Route path="/processors" element={
                                <PrivateRoute>
                                    <ProcessorsListPage />
                                </PrivateRoute>
                            } />
                            <Route path="/processors/new" element={
                                <PrivateRoute>
                                    <ProcessorEditPage />
                                </PrivateRoute>
                            } />
                            <Route path="/processors/:id" element={
                                <PrivateRoute>
                                    <ProcessorEditPage />
                                </PrivateRoute>
                            } />
                            <Route path="/waste-types" element={
                                <PrivateRoute>
                                    <PlaceholderPage title="Waste Types" />
                                </PrivateRoute>
                            } />
                            {referenceAdminAppRoutes()}
                            <Route path="/audit-logs" element={
                                <PrivateRoute>
                                    <PlaceholderPage title="Audit Logs" />
                                </PrivateRoute>
                            } />
                            <Route path={appRoutes.myTasks} element={
                                <PrivateRoute>
                                    <TaskListPage />
                                </PrivateRoute>
                            } />
                            <Route path={`${appRoutes.tasks}/:id`} element={
                                <PrivateRoute>
                                    <TaskDetailPage />
                                </PrivateRoute>
                            } />
                            <Route path={appRoutes.tasks} element={
                                <PrivateRoute roles={adminRoles}>
                                    <AllTasksPage />
                                </PrivateRoute>
                            } />
                            <Route path={appRoutes.companyDiary} element={
                                <PrivateRoute>
                                    <CalendarPage />
                                </PrivateRoute>
                            } />
                            <Route path={appRoutes.myCalendar} element={
                                <PrivateRoute>
                                    <MyCalendarPage />
                                </PrivateRoute>
                            } />
                            <Route path="/documents" element={
                                <PrivateRoute>
                                    <DocumentLibraryPage />
                                </PrivateRoute>
                            } />
                            <Route path="/checklists" element={
                                <PrivateRoute roles={adminRoles}>
                                    <ChecklistSettingsPage />
                                </PrivateRoute>
                            } />
                            <Route path={appRoutes.automaticEmails} element={
                                <PrivateRoute roles={adminRoles}>
                                    <EmailTemplateListPage />
                                </PrivateRoute>
                            } />
                            <Route path={appRoutes.automaticEmailsEdit} element={
                                <PrivateRoute roles={adminRoles}>
                                    <EmailTemplateEditPage />
                                </PrivateRoute>
                            } />
                            <Route path="/settings" element={
                                <PrivateRoute>
                                    <SettingsPage />
                                </PrivateRoute>
                            } />
                            <Route path="/users" element={
                                <PrivateRoute roles={adminRoles}>
                                    <UsersPage />
                                </PrivateRoute>
                            } />
                            <Route path="/Users" element={<Navigate to="/users" replace />} />
                            <Route path="/user" element={
                                <PrivateRoute>
                                    <UserPage />
                                </PrivateRoute>
                            } />
                            <Route path="/company" element={
                                <PrivateRoute roles={adminRoles}>
                                    <CompanyInfoPage />
                                </PrivateRoute>
                            } />
                            <Route path={appRoutes.websiteSettings} element={
                                <PrivateRoute roles={adminRoles}>
                                    <WebsiteSettingListPage />
                                </PrivateRoute>
                            } />
                            <Route path="/organisations" element={
                                <PrivateRoute roles={adminRoles}>
                                    <OrganisationsPage />
                                </PrivateRoute>
                            } />
                            <Route path={appRoutes.admin} element={
                                <PrivateRoute roles={adminRoles}>
                                    <AdminPage />
                                </PrivateRoute>
                            } />
                            <Route path="/invitations" element={
                                <PrivateRoute roles={adminRoles}>
                                    <InvitationsPage />
                                </PrivateRoute>
                            } />
                            <Route path={appRoutes.userSessions} element={
                                <PrivateRoute roles={adminRoles}>
                                    <SessionsPage />
                                </PrivateRoute>
                            } />
                            {legacyRouteElements()}
                            <Route path="/accessdenied" element={<AccessDeniedPage />} />
                            <Route path="/signin" element={<SignInPage />} />
                            <Route path="/forgotpassword" element={<ForgotPasswordPage />} />
                            <Route path="/twofactorSetup" element={<TwoFASetupPage />} />
                            <Route path="/acceptinvitation" element={<AcceptInvitationPage id={id} />} />
                            <Route path="/resetpassword" element={<ResetPasswordPage id={id} />} />
                            <Route path="*" element={<Navigate to="/dashboard" replace />} />
                        </Routes>
                    </Container>
                </div>
            </div>
            <CookieConsent
                location="bottom"
                cookieName="FroomWasteTrackerCookieConsent"
                containerClasses="cookie-consent-banner"
                style={{
                    zIndex: 7,
                    backgroundColor: "var(--bg-tertiary)",
                    alignItems: "center",
                    padding: "12px 16px",
                    flexWrap: "wrap",
                    gap: "12px",
                }}
                contentStyle={{
                    flex: "1 1 280px",
                    color: "var(--text-on-tertiary-3)",
                    margin: 0,
                }}
                buttonText="Accept all cookies"
                buttonStyle={{
                    backgroundColor: "var(--black)",
                    color: "var(--white)",
                    border: "1px solid var(--black)",
                    padding: "10px 18px",
                    fontWeight: 600,
                    borderRadius: "0.375rem",
                }}
                enableDeclineButton
                declineButtonText="More information"
                declineButtonStyle={{
                    backgroundColor: "var(--white)",
                    color: "var(--black)",
                    border: "1px solid var(--black)",
                    padding: "10px 18px",
                    fontWeight: 600,
                    borderRadius: "0.375rem",
                }}
                flipButtons
            >
                <h3 className="cookie-consent-heading">This website uses cookies</h3>
                Cookies help us deliver the best experience on our website. By using our
                website, you agree to the use of cookies.
            </CookieConsent>
            <ToastContainer />
        </>
        </SidebarLayoutProvider>
    );
};
export { App };
